import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Formik } from 'formik';
import { ConnectedFocusError } from 'focus-formik-error';
import { isEmpty } from 'lodash';
import {
  useBreakpointValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react';

import { Box } from 'src/components/UI/Box';
import { FormControl } from 'src/components/UI/FormControl';
import { Flex } from 'src/components/UI/Flex';
import { Button } from 'src/components/UI/Button';
import { Checkbox } from 'src/components/UI/Checkbox';
import { Skeleton } from 'src/components/UI/Skeleton';
import { MASKS } from 'src/components/UI/Input/MaskedInput';
import MevoInput from 'src/components/Input';
import Typography from 'src/components/Typography';
import SelectState from 'src/components/SelectState';

import { onlyDigits } from 'src/helpers/string';
import { CEPService } from 'src/services/cep';
import { placeOfServiceSchema } from './schemas/placeOfServiceSchema';
import {
  trackOnboardingPlaceOfServiceView,
  trackOnboardingPlaceOfServiceContinueClick,
  trackOnboardingWhatsMyCepClick,
} from 'src/services/tracking/events/onboarding.events';

import { type PlaceOfServiceValues } from './types';
import ArrowIcon from 'src/components/Icons/ArrowIcon';
import ExitIcon from 'src/components/Icons/ExitIcon';
import { useFeedbackContext } from 'src/providers/FeedbackProvider';

interface PlaceOfServiceStepProps {
  initialData: PlaceOfServiceValues;
  onNext: (data: PlaceOfServiceValues) => void;
  onDataChange?: (data: PlaceOfServiceValues, showForm: boolean, logoPreview: string | null) => void;
}

function AddressTooltip() {
  const { isOpen, onToggle, onClose } = useDisclosure();

  return (
    <Box position="relative" display="inline-flex">
      <Popover
        isOpen={isOpen}
        onClose={onClose}
        placement="top"
        closeOnBlur={false}
        offset={[0, 8]}
        matchWidth={false}
        isLazy
      >
        <PopoverTrigger>
          <Box
            as="span"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            w="18px"
            h="18px"
            borderRadius="full"
            bg="primary.1000"
            color="white"
            fontSize="12px"
            fontWeight="bold"
            cursor="pointer"
            onClick={onToggle}
          >
            ?
          </Box>
        </PopoverTrigger>
        <PopoverContent
          left={'-45%'}
          top={'25px'}
          bg="primary.1000"
          color="white"
          border="none"
          borderRadius="12px"
          p="20px"
          maxW="400px"
          boxShadow="0px 4px 12px rgba(0, 0, 0, 0.15)"
          _focus={{ boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)' }}
        >
          <PopoverArrow bg="primary.1000" />
          <Flex direction="column" gap="12px">
            <Flex justify="space-between" align="flex-start" gap="16px">
              <Typography model="body" size="normal" weight="bold" color="white">
                Por que pedimos seu endereço de atendimento?
              </Typography>
              <IconButton
                aria-label="Fechar"
                icon={<ExitIcon width={16} variantColor="primary.1000" baseColor="white" />}
                onClick={onClose}
                variant="ghost"
                size="sm"
                minW="20px"
                h="20px"
                color="white"
                _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
              />
            </Flex>
            <Box>
              <Box mb="12px">
                <Typography model="body" size="normal" weight="regular" color="white">
                  A Lei nº 5.991/1973 exige que toda receita tenha o endereço do consultório/serviço, além da data,
                  assinatura e registro com UF do profissional.
                </Typography>
              </Box>
              <Typography model="body" size="normal" weight="regular" color="white">
                Essas informações são obrigatórias para que a receita seja dispensada pelas farmácias.
              </Typography>
            </Box>
          </Flex>
        </PopoverContent>
      </Popover>
    </Box>
  );
}

function AddressInfoDialog({ onClose }: { onClose: () => void }) {
  return (
    <Flex direction="column" gap="12px">
      <Flex justify="space-between" align="flex-start" gap="16px">
        <Typography model="body" size="normal" weight="bold" color="white">
          Por que pedimos seu endereço de atendimento?
        </Typography>
        <IconButton
          aria-label="Fechar"
          icon={<ExitIcon width={16} variantColor="primary.1000" baseColor="white" />}
          onClick={onClose}
          variant="ghost"
          size="sm"
          minW="20px"
          h="20px"
          color="white"
          _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
        />
      </Flex>
      <Box>
        <Box mb="12px">
          <Typography model="body" size="normal" weight="regular" color="white">
            A Lei nº 5.991/1973 exige que toda receita tenha o endereço do consultório/serviço, além da data, assinatura
            e registro com UF do profissional.
          </Typography>
        </Box>
        <Typography model="body" size="normal" weight="regular" color="white">
          Essas informações são obrigatórias para que a receita seja dispensada pelas farmácias.
        </Typography>
      </Box>
    </Flex>
  );
}

export function PlaceOfServiceStep({ initialData, onNext, onDataChange }: PlaceOfServiceStepProps) {
  const isMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false });
  const { openBaseDialog } = useFeedbackContext();
  const [showForm, setShowForm] = useState(false);
  const [isSearchingCep, setIsSearchingCep] = useState(false);
  const [mask, setMask] = useState(MASKS.BRAZIL_LANDLINE_PHONE_NINE_DIGITS);
  const [hasNoNumber, setHasNoNumber] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const searchCEP = useCallback(
    async (postalCode: string, formikProps: any) => {
      const cleanPostalCode = onlyDigits(postalCode);
      if (cleanPostalCode.length === 8) {
        setIsSearchingCep(true);
        try {
          const postalCodeData = await CEPService.searchForCepInViaCep(cleanPostalCode);

          if (postalCodeData && postalCodeData.localidade && postalCodeData.uf) {
            formikProps.setFieldValue('address', postalCodeData.logradouro || '');
            formikProps.setFieldValue('district', postalCodeData.bairro || '');
            formikProps.setFieldValue('complement', postalCodeData.complemento || '');
            formikProps.setFieldValue('city', postalCodeData.localidade);
            formikProps.setFieldValue('state', postalCodeData.uf);
          } else {
            formikProps.setFieldValue('address', postalCodeData?.logradouro || '');
            formikProps.setFieldValue('district', postalCodeData?.bairro || '');
            formikProps.setFieldValue('complement', postalCodeData?.complemento || '');
            formikProps.setFieldValue('city', '');
            formikProps.setFieldValue('state', '');
          }

          setShowForm(true);

          if (onDataChange) {
            onDataChange(
              {
                ...formikProps.values,
                address: postalCodeData?.logradouro || '',
                district: postalCodeData?.bairro || '',
                complement: postalCodeData?.complemento || '',
                city: postalCodeData?.localidade || '',
                state: postalCodeData?.uf || '',
              },
              true,
              logoPreview
            );
          }
        } catch (e: any) {
          formikProps.setFieldValue('address', '');
          formikProps.setFieldValue('district', '');
          formikProps.setFieldValue('complement', '');
          formikProps.setFieldValue('city', '');
          formikProps.setFieldValue('state', '');
          setShowForm(true);

          if (onDataChange) {
            onDataChange(
              {
                ...formikProps.values,
                address: '',
                district: '',
                complement: '',
                city: '',
                state: '',
              },
              true,
              logoPreview
            );
          }
        } finally {
          setIsSearchingCep(false);
        }
      }
    },
    [logoPreview, onDataChange]
  );

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoUploadError(null);

      const reader = new FileReader();
      reader.onloadend = () => {
        const preview = reader.result as string;
        setLogoPreview(preview);
        setLogoUploadError(null);
        if (onDataChange) {
          onDataChange({ ...initialData }, showForm, preview);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteLogo = () => {
    setLogoPreview(null);
    setLogoUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onDataChange) {
      onDataChange({ ...initialData }, showForm, null);
    }
  };

  const getTitleAndDescription = useCallback(
    (values: PlaceOfServiceValues) => {
      const hasAddressData = !!(
        values.address &&
        values.district &&
        values.houseNumber &&
        values.placeName &&
        values.phoneNumber
      );

      if (!showForm) {
        return {
          title: 'Boas-vindas!',
          description: (
            <>
              Prescreva de qualquer lugar com a Mevo.
              <br />
              Agilidade para você, acesso fácil para seus pacientes.
            </>
          ),
        };
      } else if (!hasAddressData) {
        return {
          title: 'Quase lá! Vamos aos detalhes do endereço.',
          description:
            'Preencha os dados faltantes e confira no modelo ao lado como seu paciente receberá sua receita na Mevo.',
        };
      } else {
        return {
          title: 'Detalhes do endereço',
          description: 'Quase lá! Complemente o seu endereço com o número e demais informações',
        };
      }
    },
    [showForm]
  );

  return (
    <Box h="100%" display="flex" flexDir="column">
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        validateOnMount={false}
        initialValues={initialData}
        validationSchema={placeOfServiceSchema}
        onSubmit={(values, formik) => {
          const completeData: PlaceOfServiceValues = {
            ...values,
            postalCode: onlyDigits(values.postalCode),
            phoneNumber: onlyDigits(values.phoneNumber),
            houseNumber: hasNoNumber ? 'S/N' : values.houseNumber,
          };
          trackOnboardingPlaceOfServiceContinueClick();
          onNext(completeData);
          formik.setSubmitting(false);
        }}
      >
        {(formikProps) => {
          const submit = async () => {
            const errors = await formikProps.validateForm();
            if (isEmpty(errors)) {
              return formikProps.submitForm();
            }
          };

          const { title, description } = getTitleAndDescription(formikProps.values);

          return (
            <Box
              as="form"
              onSubmit={(e: any) => {
                e.preventDefault();
                submit();
              }}
              w="100%"
              h="100%"
              display="flex"
              flexDir="column"
            >
              {formikProps.isSubmitting && <ConnectedFocusError />}

              <Box flexShrink={0}>
                <Box mb={'12px'}>
                  <Box as={'h1'} fontFamily={'Ambit'} fontSize={30} color="#16002C" fontWeight={'700'}>
                    {title}
                  </Box>
                </Box>

                <Box mb={'40px'}>
                  <Box fontFamily={'Ambit'} fontSize={16} fontWeight={400} color="gray.900">
                    {description}
                  </Box>
                </Box>
              </Box>

              <Box
                flex="1"
                minH="0"
                overflowY="auto"
                pr={{ base: '0px', md: '40px', lg: '40px' }}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#FFE4EB',
                    borderRadius: '3px',
                  },
                }}
              >
                <Box mb={'16px'}>
                  <Flex alignItems="center" gap="8px">
                    <Box as={'h3'} fontFamily={'Ambit'} fontSize={'16px'} fontWeight={'700'} color={'gray.800'}>
                      Local de atendimento
                    </Box>
                    {!isMobile && <AddressTooltip />}
                    {isMobile && (
                      <Box
                        as="span"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        w="18px"
                        h="18px"
                        borderRadius="full"
                        bg="primary.1000"
                        color="white"
                        fontSize="12px"
                        fontWeight="bold"
                        cursor="pointer"
                        onClick={() => {
                          openBaseDialog(AddressInfoDialog, {
                            height: '100%',
                            maxHeight: '150px',
                            maxWidth: '90%',
                            width: 'auto',
                            padding: '20px',
                            borderRadius: '12px',
                            backgroundColor: '#3D0E6B',
                          });
                        }}
                      >
                        ?
                      </Box>
                    )}
                  </Flex>
                </Box>

                <Flex alignItems="center" gap="16px" justifyContent="center">
                  <FormControl isInvalid={!!formikProps.errors.postalCode} flex="1">
                    <MevoInput
                      id="postalCode"
                      labelText=""
                      h={'48px'}
                      type={'tel'}
                      placeholderText={'Digite o CEP do seu consultório ou clínica'}
                      name={'postalCode'}
                      value={formikProps.values.postalCode}
                      onChange={(e) => {
                        formikProps.setFieldValue('postalCode', e.target.value);
                        if (formikProps.errors.postalCode) {
                          formikProps.setFieldError('postalCode', undefined);
                        }
                        if (onlyDigits(e.target.value).length === 8) {
                          searchCEP(e.target.value, formikProps);
                        } else {
                          setShowForm(false);
                          if (onDataChange) {
                            onDataChange({ ...formikProps.values, postalCode: e.target.value }, false, logoPreview);
                          }
                        }
                      }}
                      errorText={formikProps.errors.postalCode}
                      mask={MASKS.BRAZIL_POSTAL_CODE}
                      maskChar={''}
                      autoFocus
                    />
                  </FormControl>

                  <Button
                    variant="ghost"
                    color={'primary.1000'}
                    textDecoration={'underline'}
                    fontSize={'14px'}
                    p={0}
                    h="48px"
                    minW="auto"
                    fontWeight="normal"
                    whiteSpace="nowrap"
                    _hover={{ bg: 'transparent' }}
                    onClick={() => {
                      trackOnboardingWhatsMyCepClick();
                      window.open('https://buscacepinter.correios.com.br/app/endereco/index.php', '_blank', 'noopener');
                    }}
                  >
                    Não sei o CEP
                  </Button>
                </Flex>

                {isSearchingCep && (
                  <Box>
                    <Flex gap="12px" mb={{ base: '16px', lg: '20px' }}>
                      <Box flex="2">
                        <Skeleton height="12px" width="80px" mb="8px" />
                        <Skeleton height="45px" borderRadius="8px" />
                      </Box>
                      <Box flex="1">
                        <Skeleton height="12px" width="60px" mb="8px" />
                        <Skeleton height="45px" borderRadius="8px" />
                      </Box>
                    </Flex>

                    <Flex gap="12px" mb={{ base: '16px', lg: '20px' }}>
                      <Box flex="2">
                        <Skeleton height="12px" width="120px" mb="8px" />
                        <Skeleton height="45px" borderRadius="8px" />
                      </Box>
                      <Box flex="1">
                        <Skeleton height="12px" width="60px" mb="8px" />
                        <Skeleton height="45px" borderRadius="8px" />
                      </Box>
                      <Skeleton height="45px" width="100px" mt="24px" borderRadius="8px" />
                    </Flex>

                    <Flex gap="12px" mb={{ base: '16px', lg: '20px' }}>
                      <Box flex="2">
                        <Skeleton height="12px" width="60px" mb="8px" />
                        <Skeleton height="45px" borderRadius="8px" />
                      </Box>
                      <Box flex="1">
                        <Skeleton height="12px" width="40px" mb="8px" />
                        <Skeleton height="45px" borderRadius="8px" />
                      </Box>
                    </Flex>

                    <Flex gap="12px" mb={{ base: '16px', lg: '20px' }}>
                      <Box flex="1">
                        <Skeleton height="45px" borderRadius="8px" />
                      </Box>
                      <Box flex="1">
                        <Skeleton height="45px" borderRadius="8px" />
                      </Box>
                    </Flex>

                    <Box mb={{ base: '16px', lg: '20px' }}>
                      <Skeleton height="12px" width="200px" mb="8px" />
                      <Skeleton height="12px" width="250px" mb="12px" />
                      <Skeleton height="120px" borderRadius="12px" />
                    </Box>

                    <Skeleton height="56px" borderRadius="8px" />
                  </Box>
                )}

                {showForm && (
                  <Box pb="20px">
                    <Flex gap="12px" mb={{ base: '16px', lg: '0' }} flexDirection={{ base: 'column', lg: 'row' }}>
                      <FormControl flex={1} isInvalid={!!formikProps.errors.address}>
                        <MevoInput
                          id="address"
                          h={'45px'}
                          placeholderText={'Endereço'}
                          name={'address'}
                          value={formikProps.values.address}
                          onChange={(e) => {
                            formikProps.handleChange(e);
                            if (onDataChange) {
                              onDataChange({ ...formikProps.values, address: e.target.value }, showForm, logoPreview);
                            }
                          }}
                          onBlur={() => formikProps.validateField('address')}
                          errorText={formikProps.errors.address}
                        />
                      </FormControl>
                      <FormControl flex="1" isInvalid={!!formikProps.errors.district}>
                        <MevoInput
                          id="district"
                          h={'45px'}
                          placeholderText={'Bairro'}
                          name={'district'}
                          value={formikProps.values.district}
                          onChange={(e) => {
                            formikProps.handleChange(e);
                            if (onDataChange) {
                              onDataChange({ ...formikProps.values, district: e.target.value }, showForm, logoPreview);
                            }
                          }}
                          onBlur={() => formikProps.validateField('district')}
                          errorText={formikProps.errors.district}
                        />
                      </FormControl>
                    </Flex>

                    <Flex
                      gap="12px"
                      mb={{ base: '16px', lg: '0' }}
                      flexDirection={{ base: 'column', lg: 'row' }}
                      alignItems={{ base: 'stretch', lg: 'flex-start' }}
                    >
                      <FormControl flex={1} isInvalid={!!formikProps.errors.complement}>
                        <MevoInput
                          id="complement"
                          h={'45px'}
                          placeholderText={'Complemento, ex: Bloco'}
                          name={'complement'}
                          value={formikProps.values.complement}
                          onChange={(e) => {
                            formikProps.handleChange(e);
                            if (onDataChange) {
                              onDataChange(
                                { ...formikProps.values, complement: e.target.value },
                                showForm,
                                logoPreview
                              );
                            }
                          }}
                          errorText={formikProps.errors.complement}
                        />
                      </FormControl>
                      <Flex flex="1" gap="12px" alignItems="center">
                        <FormControl flex="1" isInvalid={!!formikProps.errors.houseNumber}>
                          <MevoInput
                            id="houseNumber"
                            h={'45px'}
                            type={'tel'}
                            placeholderText={'Número'}
                            name={'houseNumber'}
                            value={hasNoNumber ? '' : formikProps.values.houseNumber}
                            onChange={(e) => {
                              formikProps.handleChange(e);
                              if (onDataChange) {
                                onDataChange(
                                  { ...formikProps.values, houseNumber: e.target.value },
                                  showForm,
                                  logoPreview
                                );
                              }
                            }}
                            onBlur={() => formikProps.validateField('houseNumber')}
                            errorText={formikProps.errors.houseNumber}
                            disabled={hasNoNumber}
                            mask={'99999'}
                            maskChar={''}
                          />
                        </FormControl>
                        <Flex alignItems="center" h="45px">
                          <Checkbox
                            isChecked={hasNoNumber}
                            onChange={(e) => {
                              setHasNoNumber(e.target.checked);
                              if (e.target.checked) {
                                formikProps.setFieldValue('houseNumber', 'S/N');
                                formikProps.setFieldError('houseNumber', undefined);
                                if (onDataChange) {
                                  onDataChange({ ...formikProps.values, houseNumber: 'S/N' }, showForm, logoPreview);
                                }
                              } else {
                                formikProps.setFieldValue('houseNumber', '');
                                formikProps.setFieldError('houseNumber', undefined);
                                if (onDataChange) {
                                  onDataChange({ ...formikProps.values, houseNumber: '' }, showForm, logoPreview);
                                }
                              }
                            }}
                          >
                            <Typography model="body" size="small">
                              Sem número
                            </Typography>
                          </Checkbox>
                        </Flex>
                      </Flex>
                    </Flex>

                    <Flex
                      gap="12px"
                      mb={{ base: '16px', lg: '0' }}
                      flexDirection={{ base: 'column', lg: 'row' }}
                      alignItems={{ base: 'stretch', lg: 'center' }}
                    >
                      <FormControl flex={{ base: '1', lg: '2' }} isInvalid={!!formikProps.errors.city}>
                        <MevoInput
                          id="city"
                          h={'45px'}
                          placeholderText={'Cidade'}
                          name={'city'}
                          value={formikProps.values.city}
                          onChange={(e) => {
                            formikProps.handleChange(e);
                            if (onDataChange) {
                              onDataChange({ ...formikProps.values, city: e.target.value }, showForm, logoPreview);
                            }
                          }}
                          onBlur={() => formikProps.validateField('city')}
                          errorText={formikProps.errors.city}
                        />
                      </FormControl>
                      <FormControl flex="1" isInvalid={!!formikProps.errors.state}>
                        <SelectState
                          placeholder={'UF'}
                          value={formikProps.values.state}
                          onChange={(value) => {
                            formikProps.setFieldValue('state', value);
                            if (onDataChange) {
                              onDataChange({ ...formikProps.values, state: value }, showForm, logoPreview);
                            }
                          }}
                          onBlur={() => formikProps.validateField('state')}
                          errorMessage={formikProps.errors.state}
                        />
                      </FormControl>
                    </Flex>

                    <Flex
                      gap="12px"
                      mb={{ base: '16px', lg: '20px' }}
                      flexDirection={{ base: 'column', lg: 'row' }}
                      alignItems={{ base: 'stretch', lg: 'flex-start' }}
                    >
                      <FormControl flex="1" isInvalid={!!formikProps.errors.placeName}>
                        <MevoInput
                          id="placeName"
                          h={'45px'}
                          type={'text'}
                          placeholderText={'Nome do consultório ou clínica'}
                          name={'placeName'}
                          value={formikProps.values.placeName}
                          onChange={(e) => {
                            formikProps.handleChange(e);
                            if (onDataChange) {
                              onDataChange({ ...formikProps.values, placeName: e.target.value }, showForm, logoPreview);
                            }
                          }}
                          errorText={formikProps.errors.placeName}
                          onBlur={() => formikProps.validateField('placeName')}
                        />
                      </FormControl>

                      <FormControl flex="1" isInvalid={!!formikProps.errors.phoneNumber}>
                        <MevoInput
                          id="phoneNumber"
                          h={'45px'}
                          type={'tel'}
                          placeholderText={'(DDD) 9 0000-0000'}
                          name={'phoneNumber'}
                          value={formikProps.values.phoneNumber}
                          onChange={(e) => {
                            formikProps.handleChange(e);
                            if (onDataChange) {
                              onDataChange(
                                { ...formikProps.values, phoneNumber: e.target.value },
                                showForm,
                                logoPreview
                              );
                            }
                          }}
                          errorText={formikProps.errors.phoneNumber}
                          mask={mask}
                          onBlur={(e) => {
                            formikProps.validateField('phoneNumber');
                            if (onlyDigits(e.target.value).length === 10) {
                              setMask(MASKS.BRAZIL_LANDLINE_PHONE);
                            }
                            if (onlyDigits(e.target.value).length === 11) {
                              setMask(MASKS.BRAZIL_LANDLINE_PHONE_NINE_DIGITS);
                            }
                          }}
                          onFocus={() => {
                            setMask('(99) 999999999');
                          }}
                        />
                      </FormControl>
                    </Flex>

                    <Box mb={{ base: '16px', lg: '60px' }}>
                      <Box mb="8px">
                        <Box as={'h3'} fontFamily={'Ambit'} fontSize={'16px'} fontWeight={'regular'} color="gray.800">
                          <b>Seu logotipo profissional:</b> (opcional):
                        </Box>
                      </Box>
                      <Box mb="12px">
                        <Box as={'span'} fontFamily={'Ambit'} fontWeight={'400'} fontSize="14px" color="gray.800">
                          Formatos permitidos: .jpeg ou .png (proporção de 16x5)
                        </Box>
                      </Box>
                      <Box
                        border="2px solid"
                        borderColor={logoUploadError ? '#E53E3E' : 'gray.300'}
                        borderRadius="12px"
                        p="24px"
                        textAlign="center"
                        cursor={logoPreview ? 'default' : 'pointer'}
                        bg={logoPreview ? 'gray.50' : 'white'}
                        _hover={
                          !logoPreview
                            ? { borderColor: logoUploadError ? '#C53030' : 'primary.500', bg: 'gray.50' }
                            : {}
                        }
                        onClick={() => !logoPreview && fileInputRef.current?.click()}
                      >
                        {logoPreview ? (
                          <Box>
                            <Box
                              w="100%"
                              h="120px"
                              overflow="hidden"
                              borderRadius="8px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <img
                                src={logoPreview}
                                alt="Preview do logotipo"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Box>
                            <Box fontSize="32px" mb="8px">
                              ☁️
                            </Box>
                            <Box fontFamily={'Ambit'} as="p" fontSize="12px" fontWeight={'400'} color="gray.700">
                              Tamanho máximo: 512 KB
                            </Box>
                            <Box mt="4px">
                              <Box fontFamily={'Ambit'} as="p" fontSize="14px" fontWeight={'400'} color="gray.800">
                                Clique para adicionar
                              </Box>
                            </Box>
                          </Box>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png"
                          style={{ display: 'none' }}
                          onChange={handleLogoUpload}
                        />
                      </Box>

                      {logoPreview && (
                        <Flex gap="10px" mt="12px" justifyContent="flex-end">
                          <Button
                            variant="outline"
                            borderColor="#E8E8E8"
                            bg="white"
                            color="#E53E3E"
                            h="38px"
                            px="16px"
                            fontSize="13px"
                            fontWeight="500"
                            borderRadius="8px"
                            leftIcon={
                              <Box as="span" mr="2px">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                  <path
                                    d="M2 4H14M5.5 4V2.5C5.5 2.22386 5.72386 2 6 2H10C10.2761 2 10.5 2.22386 10.5 2.5V4M12.5 4V13.5C12.5 13.7761 12.2761 14 12 14H4C3.72386 14 3.5 13.7761 3.5 13.5V4"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </Box>
                            }
                            _hover={{ bg: '#FEF2F2', borderColor: '#E53E3E' }}
                            onClick={handleDeleteLogo}
                          >
                            Excluir
                          </Button>
                          <Button
                            variant="outline"
                            borderColor="#E8E8E8"
                            bg="white"
                            color="#4A5568"
                            h="38px"
                            px="16px"
                            fontSize="13px"
                            fontWeight="500"
                            borderRadius="8px"
                            leftIcon={
                              <Box as="span" mr="2px">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                  <path
                                    d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1738 1.49653 12.4191 1.44775 12.6667 1.44775C12.9142 1.44775 13.1595 1.49653 13.3883 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5036 2.84055 14.5523 3.08588 14.5523 3.33337C14.5523 3.58087 14.5036 3.8262 14.4088 4.05497C14.314 4.28375 14.1751 4.49162 14 4.66671L5 13.6667L1.33333 14.6667L2.33333 11L11.3333 2.00004Z"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </Box>
                            }
                            _hover={{ bg: 'gray.50', borderColor: 'gray.400' }}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            Alterar
                          </Button>
                        </Flex>
                      )}
                      {logoUploadError && (
                        <Box mt="8px" color="#E53E3E">
                          <Typography model="body" size="small">
                            {logoUploadError}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Flex
                      alignItems="flex-end"
                      pointerEvents={'none'}
                      position={isMobile ? 'fixed' : 'absolute'}
                      bottom={0}
                      paddingBottom={isMobile ? '20px' : '0'}
                      background={'linear-gradient(to bottom, rgba(255, 255, 255, -2.5),  rgba(255, 255, 255, 4.5))'}
                      left="0"
                      right="0"
                      px={isMobile ? '20px' : '0'}
                      zIndex="998"
                      justifyContent="end"
                      mt="auto"
                      pt="24px"
                      flexDirection={isMobile ? 'row-reverse' : 'column'}
                    >
                      <Button
                        h={isMobile ? '60px' : '44px'}
                        w={isMobile ? '60px' : 'auto'}
                        pointerEvents={'all'}
                        type="submit"
                        px="20px"
                        fontSize="14px"
                        variant="solid"
                        colorScheme="primary"
                        isLoading={formikProps.isSubmitting}
                        rightIcon={
                          !isMobile ? (
                            <Box
                              w="24px"
                              h="24px"
                              borderRadius="full"
                              bg="white"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              ml="8px"
                            >
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                <path
                                  d="M6 12L10 8L6 4"
                                  stroke="#7C3AED"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </Box>
                          ) : undefined
                        }
                      >
                        {isMobile ? <ArrowIcon direction="right" width={10} variantColor="white" /> : 'Próximo'}
                      </Button>
                    </Flex>
                  </Box>
                )}
              </Box>
            </Box>
          );
        }}
      </Formik>
    </Box>
  );
}
